import { ReactElement } from "react";
import { IAxis, IAxisLinear } from "../../../../components/charts/types";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../components/charts/chartUtils";
import AxisHorizontal from "../../../../components/charts/AxisHorizontal";
import AxisVertical from "../../../../components/charts/AxisVertical";
import Chart from "../../../../components/charts/chart";
import * as d3 from "d3";
import { getPalette } from "../../../../configuration/configuration";
import KeyValueItem from "../../../../components/charts/keyValueItem";
import { formatTimeWithSeconds } from "../../../../system/utils";
import HtmlTooltip from "../../../../components/charts/tooltip";
import { IMillProductDetail } from "../../types";
import Grid from "../../../../components/charts/grid";

export interface IPoint {
  title: string;
  beginDatetime: number;
  endDatetime: number;
  value: number;
  data: IMillProductDetail;
}

export interface ICustomSeries {
  name: string;
  points: IPoint[];
  color: string;
  unit?: string;
}

export interface IProductChartConfig {
  width?: number;
  height?: number;
  series: ICustomSeries;
  title?: string;
  xAxis: IAxis;
  yAxis: IAxis;
  showLegend?: boolean;
}

interface IProps {
  chartConfig: IProductChartConfig;
  selectedItem: IMillProductDetail | null;
}

const ProductDetailChart = ({ chartConfig, selectedItem }: IProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: chartConfig.height ?? undefined,
    width: chartConfig.width ?? undefined,
    marginBottom: 50,
    marginLeft: 10,
    marginRight: 70,
    marginTop: 20
  });

  const lineID = useUniqueId("product-detail-chart");

  const axisLinearX = chartConfig.xAxis as IAxisLinear;
  const axisLinearY = chartConfig.yAxis as IAxisLinear;

  const xScale = d3.scaleLinear()
    .domain([axisLinearX.minimum, axisLinearX.maximum])
    .range([0, dimensions.boundedWidth]);

  const yScale = d3.scaleLinear()
    .domain([axisLinearY.minimum ?? 0, axisLinearY.maximum ?? 1])
    .range([dimensions.boundedHeight, 0]);

  const x1Accessor = (d: IPoint) => d.beginDatetime;
  const x2Accessor = (d: IPoint) => d.endDatetime;
  const yAccessor = (d: IPoint) => d.value;
  const x1AccessorScaled = (d: IPoint) => xScale(x1Accessor(d));
  const x2AccessorScaled = (d: IPoint) => xScale(x2Accessor(d));
  const yAccessorScaled = (d: IPoint) => yScale(yAccessor(d));

  const ticksX = getTicks(xScale.domain(), axisLinearX.ticksCount);
  const ticksY = getTicks(yScale.domain(), axisLinearY.ticksCount);


  return (
    <div ref={ref} style={{ width: "100%", height: "calc(100% - 10px)", marginBottom: 10, borderTop: "1px solid #ddd" }} >
      <Chart dimensions={dimensions} >
        <AxisHorizontal
          chartID={lineID}
          dimensions={dimensions}
          scale={xScale}
          label={chartConfig.xAxis.name}
          ticks={ticksX}
          formatTick={chartConfig.xAxis.formatTick}
        />

        <AxisVertical
          chartID={lineID}
          dimensions={dimensions}
          scale={yScale}
          label={chartConfig.yAxis.name}
          ticks={ticksY}
          formatTick={chartConfig.yAxis.formatTick}/>

        <Grid charID={lineID} xScale={xScale} yScale={yScale} dimensions={dimensions} xTicks={ticksX} yTicks={ticksY}/>

        <g>
          {chartConfig.series.points.map((point, index) => {

            const rectWidth = x2AccessorScaled(point) - x1AccessorScaled(point);
            const flag = rectWidth / point.title.length > 8.3;

            return(
              <g key={`${lineID}-${index}`}>
                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-column align-items-center" >
                      <strong>Coil</strong>
                      <strong>{point.data.billet}</strong>
                    </div>
                    <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(point.beginDatetime)} />
                    <KeyValueItem name={"End"} value={formatTimeWithSeconds(point.endDatetime)} />
                    <KeyValueItem name={"Weight"} value={point.value.toFixed(0)}  unit={chartConfig.series.unit} />
                  </div>
                }>
                  <g>
                    <rect
                      x={x1AccessorScaled(point)}
                      y={yAccessorScaled(point) - 10}
                      width={rectWidth}
                      height={20}
                      fill={selectedItem && `${selectedItem.billet}` === point.title ? getPalette().statusColor.selected.background : getPalette().statusColor.empty.background}
                    />

                    <text
                      y={yAccessorScaled(point) + 5}
                      x={x1AccessorScaled(point) + rectWidth / 2 }
                      fontSize="1em"
                      textAnchor="middle"
                      fill={flag ? "white" : "#444"}
                      fontWeight={"bold"}>
                      {point.title}
                    </text>
                  </g>
                </HtmlTooltip>
              </g>
            );
          })
          }
        </g>

      </Chart>
    </div>
  );
};

export default ProductDetailChart;
